import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'

import { StusdsRiskParameters } from '../../common/logic/stusdsRiskParameters'
import { formatPercentage } from '@/domain/common/format'
import { Token, USD_MOCK_TOKEN } from '@/domain/types/Token'
import { assets } from '@/ui/assets'
import AlertCircleIcon from '@/ui/assets/icons/alert-circle.svg?react'
import ErrorCircleIcon from '@/ui/assets/icons/error-circle.svg?react'
import SuccessIcon from '@/ui/assets/icons/success.svg?react'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { links } from '@/ui/constants/links'
import { testIds } from '@/ui/utils/testIds'

export interface StusdsWelcomeViewProps extends StusdsRiskParameters {
  totalAssets: NormalizedNumber
  lockstakeDebt: NormalizedNumber
  usds: Token
  onConfirm: () => void
}

export function StusdsWelcomeView({
  lockstakeLtv,
  skyMaxPriceCap,
  liquidationEnabled,
  usds,
  totalAssets,
  lockstakeDebt,
  onConfirm,
}: StusdsWelcomeViewProps) {
  const liquidity = totalAssets.minus(lockstakeDebt)
  const fillPercentage = Percentage(totalAssets.div(lockstakeDebt), { allowMoreThan1: true })
  const lockstakeLtvText = formatPercentage(lockstakeLtv, { minimumFractionDigits: 0 })

  const liquidationEnabledText = liquidationEnabled ? 'enabled' : 'disabled'

  const skyMaxPriceCapText = USD_MOCK_TOKEN.formatUSD(skyMaxPriceCap)

  return (
    <div>
      <img
        src={assets.savings.stusdsWelcomeBg}
        width={686}
        height={207}
        alt="stusds-welcome"
        className="w-full bg-primary-inverse"
      />
      <div className="flex flex-col gap-2 p-6 md:gap-4 md:p-8">
        <div className="flex flex-col gap-2 md:gap-4">
          <div className="flex flex-col gap-2 md:gap-4">
            <div className="typography-heading-5 text-primary">Welcome to Sky stUSDS</div>
            <div className="typography-body-3 text-secondary">
              stUSDS (Staked USDS) is a vault offered by Sky that lends deposited USDS exclusively to borrowers who lock
              SKY tokens as collateral in the Sky Lockstake Engine. In return for supplying this lending capital,
              depositors earn the Sky Staked USDS Rate.{' '}
              <span className="whitespace-nowrap">
                <ExternalLink to={links.docs.savings.stusds} variant="secondary">
                  Learn more about stUSDS
                </ExternalLink>
              </span>
            </div>
          </div>
          <ul className="flex flex-col gap-1 text-primary md:gap-2">
            <ListItem type="positive">
              <ListItem.Title>Higher Annual Percentage Yield</ListItem.Title>
              <ListItem.Content>
                stUSDS offers a higher APY than Savings Vaults. In exchange, depositors take on a higher risk profile,
                including potential haircuts if collateral liquidations don't cover borrower debt.
              </ListItem.Content>
            </ListItem>
            <ListItem type="warning">
              <ListItem.Title>Collateral Parameters</ListItem.Title>
              <ListItem.Content>
                Borrowers use Staked SKY as collateral. Loans feature a maximum loan-to-value (LTV) ratio of{' '}
                <span data-testid={testIds.dialog.savings.welcomeStusds.lockstakeLtv}>{lockstakeLtvText}</span>, with
                liquidations{' '}
                <span data-testid={testIds.dialog.savings.welcomeStusds.liquidationEnabled}>
                  {liquidationEnabledText}
                </span>{' '}
                and the SKY oracle price capped at a maximum of{' '}
                <span data-testid={testIds.dialog.savings.welcomeStusds.skyMaxPriceCap}>{skyMaxPriceCapText}</span> for
                loan calculations.
              </ListItem.Content>
            </ListItem>
            {liquidity.lte(0) && (
              <ListItem type="error">
                <ListItem.Title>Temporary Illiquidity During Bootstrapping</ListItem.Title>
                <ListItem.Content>
                  Until deposits exceed {usds.format(lockstakeDebt, { style: 'compact' })} USDS, utilization will remain
                  above 100% and withdrawals may be temporarily unavailable. Current deposits:{' '}
                  {usds.format(totalAssets, { style: 'compact' })}/{usds.format(lockstakeDebt, { style: 'compact' })} (
                  {formatPercentage(fillPercentage, { minimumFractionDigits: 2 })} filled).
                </ListItem.Content>
              </ListItem>
            )}
          </ul>
        </div>
        <div className="flex flex-col items-center gap-2 md:gap-3">
          <Button
            className="w-full"
            size="l"
            onClick={onConfirm}
            data-testid={testIds.dialog.savings.welcomeStusds.okButton}
          >
            OK, I understand
          </Button>
          <div className="typography-body-3 text-center text-secondary tracking-tighter! sm:tracking-normal!">
            By clicking “OK, I understand”, you confirm that you&apos;re aware of the risks associated with higher-yield
            vaults.
          </div>
        </div>
      </div>
    </div>
  )
}

function ListItemTitle({ children }: { children: React.ReactNode }) {
  return <div className="typography-label-3 text-primary">{children}</div>
}

function ListItemContent({ children }: { children: React.ReactNode }) {
  return <div className="typography-body-4 text-secondary">{children}</div>
}
interface ListItemProps {
  type: 'positive' | 'warning' | 'error'
  children: React.ReactNode
}

function ListItem({ type, children }: ListItemProps) {
  return (
    <li className="typography-label-3 flex items-center gap-2.5">
      {(() => {
        switch (type) {
          case 'positive':
            return <SuccessIcon className="icon-size-xs sm:icon-size-md icon-system-success-primary shrink-0" />
          case 'warning':
            return <AlertCircleIcon className="icon-size-xs sm:icon-size-md shrink-0" />
          case 'error':
            return <ErrorCircleIcon className="icon-size-xs sm:icon-size-md shrink-0" />
        }
      })()}
      <div className="flex flex-col gap-0.5 md:gap-1">{children}</div>
    </li>
  )
}

ListItem.Title = ListItemTitle
ListItem.Content = ListItemContent
